// Product tabs: an ARIA tablist over collection panels. A panel's cards load from the collection-tabs-ajax
// section, once and one request at a time (a second call awaits the one in flight): the next tab ahead of the
// click (when the tabs come into view, then after each tab opens, or on hover and focus of a tab), and at the
// latest when its tab opens, while placeholder cards hold the space. The first tab is rendered with its first
// step only; its first Load more fetches the rest from the same endpoint. Load more reveals the next step of
// cards; once every card is shown (or the maximum is reached) it gives way to the link to the collection.
// A failed load keeps the placeholders and shows a message with Retry (and the collection link).
if (!customElements.get('product-tabs')) {
	class ProductTabs extends HTMLElement {
		connectedCallback() {
			if (this.tabs) return;
			this.step = parseInt(this.dataset.step, 10) || 8;
			this.max = parseInt(this.dataset.max, 10) || 16;
			this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
			this.tabs.forEach((tab, index) => {
				const warm = () => this.warm(this.panelOf(tab));
				tab.addEventListener('click', () => this.select(index));
				tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
				tab.addEventListener('pointerenter', warm);
				tab.addEventListener('focus', warm);
			});
			this.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
				panel.querySelector('.product-tabs--more').addEventListener('click', () => this.showMore(panel));
				panel.querySelector('.product-tabs--retry').addEventListener('click', () => this.retry(panel));
				this.update(panel);
			});
			this.prefetchWhenSeen();
			// Theme editor: choosing a Tab block in the sidebar opens its tab (the event comes from the tab button).
			if (window.Shopify && Shopify.designMode) {
				this.addEventListener('shopify:block:select', (event) => {
					const index = this.tabs.indexOf(event.target);
					if (index !== -1) this.select(index);
				});
			}
		}
		disconnectedCallback() {
			if (this.observer) this.observer.disconnect();
		}
		panelOf(tab) {
			return document.getElementById(tab.getAttribute('aria-controls'));
		}
		// Only the tab after the open one is fetched ahead, once the shopper reaches the section, unless data saving is on.
		prefetchWhenSeen() {
			if (this.tabs.length < 2) return;
			this.observer = new IntersectionObserver(
				(entries) => {
					if (!entries.some((entry) => entry.isIntersecting)) return;
					this.observer.disconnect();
					const run = () => this.prefetchAfter(this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
					if ('requestIdleCallback' in window) requestIdleCallback(run);
					else setTimeout(run, 200);
				},
				{ rootMargin: '300px' }
			);
			this.observer.observe(this);
		}
		prefetchAfter(index) {
			if (navigator.connection && navigator.connection.saveData) return;
			const next = this.tabs[index + 1];
			if (next) this.warm(this.panelOf(next));
		}
		onKeydown(event, index) {
			const keys = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: this.tabs.length - 1 };
			if (!(event.key in keys)) return;
			event.preventDefault();
			const next = (keys[event.key] + this.tabs.length) % this.tabs.length;
			this.select(next);
			this.tabs[next].focus();
		}
		select(index) {
			this.tabs.forEach((tab, i) => {
				const selected = i === index;
				tab.setAttribute('aria-selected', selected ? 'true' : 'false');
				tab.tabIndex = selected ? 0 : -1;
				this.panelOf(tab).hidden = !selected;
			});
			this.reveal(this.tabs[index]);
			this.alignPage(this.tabs[index]);
			this.warm(this.panelOf(this.tabs[index]));
			this.prefetchAfter(index);
		}
		// Scrolls the tab row (not the page) so the chosen tab sits fully in view.
		reveal(tab) {
			const list = tab.parentElement;
			const tabBox = tab.getBoundingClientRect();
			const listBox = list.getBoundingClientRect();
			if (tabBox.left < listBox.left || tabBox.right > listBox.right) {
				list.scrollBy({ left: tabBox.left - listBox.left - (listBox.width - tabBox.width) / 2, behavior: 'smooth' });
			}
		}
		// When the shopper has scrolled into the products (the tab row stuck at its sticky top, or scrolled away when
		// it does not stick), the page moves so the row sits at its sticky position with the new panel's first cards
		// right below it. With the row in its natural place the page stays still. Focus is left alone.
		// The target is the header's resting edge (--header-bottom): scrolling up brings a slid-away header back,
		// and the row then pins under it (app.css --sticky-top).
		alignPage(tab) {
			const list = tab.parentElement;
			const panel = this.panelOf(tab);
			const listStyle = getComputedStyle(list);
			const listBox = list.getBoundingClientRect();
			const gap = parseFloat(listStyle.marginBottom) || 0;
			const panelTop = panel.getBoundingClientRect().top;
			const sticky = listStyle.position === 'sticky';
			const currentTop = sticky ? parseFloat(listStyle.top) || 0 : 0;
			if (Math.min(listBox.top - currentTop, panelTop - listBox.bottom - gap) >= -1) return;
			const restingTop = sticky ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-bottom')) || 0 : 0;
			const instant = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			window.scrollBy({ top: panelTop - restingTop - listBox.height - gap, behavior: instant ? 'instant' : 'smooth' });
		}
		isPending(panel) {
			return Boolean(panel.querySelector('.product-tabs--grid > .product-card-placeholder'));
		}
		// Loads a panel that still shows placeholder cards; a panel with real cards waits for Load more.
		warm(panel) {
			return this.isPending(panel) ? this.load(panel) : Promise.resolve();
		}
		// Fetches the panel's cards (up to the maximum) and adds those it does not show yet. Single flight: a call
		// while a request is in flight gets that request's promise. Resolves either way; a failure sets data-error.
		load(panel) {
			if (panel.loading) return panel.loading;
			if (!panel.dataset.url || !(this.isPending(panel) || panel.hasAttribute('data-partial'))) return Promise.resolve();
			// A retry clears the message while it runs; focus leaves the Retry button for the panel before it hides,
			// and comes back to Retry if the request fails again (also when it was started from Load more).
			const retryButton = panel.querySelector('.product-tabs--retry');
			const fromButton = retryButton === document.activeElement || panel.querySelector('.product-tabs--more') === document.activeElement;
			if (panel.dataset.error) {
				if (retryButton === document.activeElement) panel.focus({ preventScroll: true });
				delete panel.dataset.error;
				this.update(panel);
			}
			panel.setAttribute('aria-busy', 'true');
			panel.loading = this.fetchCards(panel)
				.catch((error) => {
					panel.dataset.error = 'true';
					console.error(error);
				})
				.finally(() => {
					panel.loading = null;
					panel.removeAttribute('aria-busy');
					this.update(panel);
					if (panel.dataset.error && fromButton) retryButton.focus({ preventScroll: true });
				});
			return panel.loading;
		}
		async fetchCards(panel) {
			const grid = panel.querySelector('.product-tabs--grid');
			const response = await fetch(panel.dataset.url);
			if (!response.ok) throw new Error(`Product tabs request failed: ${response.status}`);
			const html = new DOMParser().parseFromString(await response.text(), 'text/html');
			const shown = grid.querySelectorAll(':scope > .columns:not(.product-card-placeholder)').length;
			const cards = Array.from(html.querySelectorAll('.columns[data-collection]')).slice(shown, this.max);
			cards.forEach((card, i) => (card.hidden = shown + i >= this.step));
			const nodes = cards.map((card) => document.importNode(card, true));
			if (shown) grid.append(...nodes);
			else grid.replaceChildren(...nodes);
			panel.removeAttribute('data-partial');
		}
		async showMore(panel) {
			if (panel.loading) return;
			if (panel.hasAttribute('data-partial')) {
				await this.load(panel);
				if (panel.dataset.error) return;
			}
			const hidden = Array.from(panel.querySelectorAll('.product-tabs--grid > [hidden]'));
			hidden.slice(0, this.step).forEach((card) => (card.hidden = false));
			this.update(panel);
			const first = hidden[0] && hidden[0].querySelector('a');
			if (first) first.focus({ preventScroll: true });
			else if (!panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
		}
		// Retry repeats what failed: the panel's first load, or the first tab's Load more.
		retry(panel) {
			return this.isPending(panel) ? this.load(panel) : this.showMore(panel);
		}
		update(panel) {
			const hiddenCount = panel.querySelectorAll('.product-tabs--grid > [hidden]').length;
			const partial = panel.hasAttribute('data-partial');
			const error = Boolean(panel.dataset.error);
			const message = panel.querySelector('.product-tabs--message');
			const text = error ? message.dataset.error : '';
			if (message.textContent !== text) message.textContent = text;
			panel.querySelector('.product-tabs--retry').hidden = !error;
			panel.querySelector('.product-tabs--more').hidden = error || (hiddenCount === 0 && !partial);
			const all = panel.querySelector('.product-tabs--all');
			if (all) all.hidden = !error && (hiddenCount > 0 || partial || this.isPending(panel));
		}
	}
	customElements.define('product-tabs', ProductTabs);
}
